/**
 * How many texts a message will actually cost, and why.
 *
 * Kept in step with `api/src/lib/sms-length.ts` — the composer warns with it,
 * the send path refuses with it, and they must agree.
 *
 * A customer hit `Message too large - The SMS message would be divided into
 * 13 parts. The maximum is 10.` with no warning beforehand and no idea what
 * to do about it.
 *
 * The part that isn't obvious: a text is 160 characters ONLY while every
 * character is in the GSM-7 alphabet. One curly apostrophe — which is what
 * you get pasting out of Word, Google Docs or a phone keyboard — forces the
 * whole message into UCS-2, where a part is 70 characters. The same words can
 * cost twice as many texts because of a punctuation mark nobody can see.
 */

/** The GSM 03.38 basic alphabet. */
const GSM_BASIC = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
);
/** Sent as an escape + the character, so each costs two. */
const GSM_EXTENDED = new Set("^{}\\[~]|€");

/** Per-part capacity. A single part gets the whole allowance. */
const LIMITS = {
  "GSM-7": { single: 160, multi: 153 },
  "UCS-2": { single: 70, multi: 67 },
} as const;

/** What a carrier will accept in one concatenated message. */
export const MAX_SMS_PARTS = 10;

export interface SmsLength {
  encoding: "GSM-7" | "UCS-2";
  /** Characters as billed: a GSM extended character counts twice. */
  units: number;
  /** Characters as a person counts them. */
  characters: number;
  parts: number;
  /** Characters still available before this becomes one more part. */
  remaining: number;
  /** Would a carrier refuse it? */
  tooLong: boolean;
  /** The characters that forced UCS-2, each listed once, in order. */
  forcedBy: string[];
}

export function smsLength(body: string): SmsLength {
  const text = body ?? "";
  // Surrogate pairs (emoji) are two UCS-2 units, so count code UNITS, not
  // code points — that's what the carrier charges for.
  const characters = [...text].length;
  const forced: string[] = [];
  let units = 0;
  let gsm = true;
  for (const ch of text) {
    if (GSM_BASIC.has(ch)) { units += 1; continue; }
    if (GSM_EXTENDED.has(ch)) { units += 2; continue; }
    gsm = false;
    if (!forced.includes(ch)) forced.push(ch);
  }
  const encoding = gsm ? "GSM-7" : "UCS-2";
  if (!gsm) units = text.length;  // UTF-16 code units
  const lim = LIMITS[encoding];
  const parts = units === 0 ? 0 : units <= lim.single ? 1 : Math.ceil(units / lim.multi);
  const capacity = parts <= 1 ? lim.single : parts * lim.multi;
  return {
    encoding, units, characters, parts,
    remaining: Math.max(0, capacity - units),
    tooLong: parts > MAX_SMS_PARTS,
    forcedBy: forced.slice(0, 8),
  };
}

/**
 * Swap the typographic characters a word processor inserts for the plain ones
 * a text can carry, without touching anything that changes the meaning. Emoji
 * and accented letters are left alone — those are the sender's choice; curly
 * quotes are not, they just happen to them.
 */
const TIDY: Array<[RegExp, string]> = [
  [/[‘’‚‛′]/g, "'"],
  [/[“”„‟″]/g, '"'],
  [/[–—―]/g, "-"],
  [/…/g, "..."],
  [/[   ]/g, " "],
  [/[•·]/g, "-"],
  [/™/g, "(TM)"],
  [/®/g, "(R)"],
  [/½/g, "1/2"],
  [/[‐‑]/g, "-"],
];

export function tidyForSms(body: string): string {
  let out = body ?? "";
  for (const [re, to] of TIDY) out = out.replace(re, to);
  return out;
}

/** True when tidying would actually change something. */
export function canTidy(body: string): boolean {
  return tidyForSms(body) !== body;
}

/** What to tell somebody whose text is too long, in words they can act on. */
export function tooLongMessage(len: SmsLength): string {
  const over = len.parts - MAX_SMS_PARTS;
  const base = `That text is too long to send - it would take ${len.parts} messages and the phone networks allow ${MAX_SMS_PARTS}.`;
  if (len.encoding === "UCS-2" && len.forcedBy.length > 0) {
    return `${base} It's mostly punctuation: ${describeForced(len.forcedBy)} more than halves how much fits in a text. Use "Fix the punctuation" to swap them, or shorten it by about ${Math.ceil((over * 67) / 10) * 10} characters.`;
  }
  return `${base} Shorten it by about ${Math.ceil((over * 153) / 10) * 10} characters, or send it as an email instead.`;
}

/** Name the offending characters the way a person would. */
export function describeForced(chars: string[]): string {
  const NAMES: Record<string, string> = {
    "’": "a curly apostrophe", "‘": "a curly quote",
    "“": "curly quotation marks", "”": "curly quotation marks",
    "—": "a long dash", "–": "a long dash",
    "…": "an ellipsis", " ": "a non-breaking space",
  };
  const named = chars.map((c) => NAMES[c] ?? (/\p{Emoji}/u.test(c) ? "an emoji" : `"${c}"`));
  const seen = [...new Set(named)];
  if (seen.length === 1) return seen[0]!;
  return `${seen.slice(0, -1).join(", ")} and ${seen[seen.length - 1]}`;
}
